import axios from "axios";
import Cookies from "js-cookie";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Row,
  Col,
  Image,
  Typography,
  Tag,
  Button,
  InputNumber,
  Space,
  Tooltip,
  Divider,
  message,
} from "antd";
import {
  CheckOutlined,
  ShoppingOutlined,
  MinusOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import { refresh } from "../../store/adminSlice";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

export default function SearchItemDetails({
  itemm,
  wege_order_id,
  getOrder,
  isDarkMode = false,
}) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [item, setItem] = useState(itemm);
  const [image, setImage] = useState(item.images?.split(",")[0] || "");
  const [hideVariation, setHideVariation] = useState(true);
  const [availableColors, setAvailableColors] = useState([]);
  const [availableSizes, setAvailablesizes] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [variation, setVariation] = useState(null);
  const [adding, setAdding] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allImages = item.images
    ? item.images.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const getImageUrl = (img) => {
    if (!img) return "";
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `${process.env.REACT_APP_PUBLIC_MEDIA_URL}store/${item.store}/${img}`;
  };

  const clearSelection = useCallback(() => {
    let sizeNames = [],
      colorNames = [],
      sizes = [],
      colors = [];
    setVariation(null);
    setImage(item.images?.split(",")[0] || "");

    item.variation?.forEach((vari) => {
      if (vari.size?.name && !sizeNames.includes(vari.size.name)) {
        sizes.push({ ...vari, stock: vari.stock !== undefined ? vari.stock : 100 });
        sizeNames.push(vari.size.name);
      }

      if (vari.color?.name && !colorNames.includes(vari.color.name)) {
        colors.push({ ...vari, stock: vari.stock !== undefined ? vari.stock : 100 });
        colorNames.push(vari.color.name);
      }
    });

    setAvailablesizes(sizes);
    if (colors.length === 1 && (!colors[0].images || colors[0].images === "")) {
      setAvailableColors(
        colors.map((vari) => ({ ...vari, images: item.images?.split(",")[0] || "" })),
      );
    } else {
      setAvailableColors(colors);
    }
  }, [item.images, item.variation]);

  const getSizes = (_variation) => {
    if (_variation.id === variation?.id) {
      clearSelection();
      setImage(item.images?.split(",")[0] || "");
    } else {
      let _sizes = [];
      let variationSize = [];
      item.variation?.forEach((vari) => {
        if (
          vari.color?.name === _variation.color?.name &&
          vari.color?.id === _variation.color?.id &&
          !_sizes.includes(vari.size?.name)
        ) {
          _sizes.push(vari.size?.name);
          variationSize.push(vari);
        }
      });
      setAvailablesizes(variationSize);
    }
  };

  const getColors = (_variation) => {
    if (_variation.id === variation?.id) {
      clearSelection();
      setImage(item.images?.split(",")[0] || "");
    } else {
      let _colors = [];
      let variationColor = [];
      item.variation?.forEach((vari) => {
        if (
          vari.size?.name === _variation.size?.name &&
          vari.size?.id === _variation.size?.id &&
          !_colors.includes(vari.color?.name)
        ) {
          _colors.push(vari.color?.name);
          variationColor.push(vari);
        }
      });

      if (variationColor.length === 1 && (!variationColor[0].images || variationColor[0].images === "")) {
        setAvailableColors(
          variationColor.map((vari) => ({
            ...vari,
            images: item.images?.split(",")[0] || "",
          })),
        );
      } else {
        setAvailableColors(variationColor);
      }
    }
  };

  const handleSelectColor = (color) => {
    if (color.stock <= 0) return;
    const isSelected =
      variation?.color?.name === color.color?.name ||
      variation?.color?.id === color.color?.id;

    if (isSelected) {
      clearSelection();
    } else {
      const colorImg =
        (!color.images || color.images === "") && availableColors.length === 1
          ? item.images?.split(",")[0]
          : color.images;
      if (colorImg) {
        setImage(colorImg);
      }
      getSizes(color);
      const matched = item.variation?.find((v) => v.id === color.id);
      setVariation(matched || null);
    }
  };

  const handleSelectSize = (size) => {
    if (size.stock <= 0) return;
    const isSelected =
      variation?.size?.name === size.size?.name ||
      variation?.size?.id === size.size?.id;

    if (isSelected) {
      clearSelection();
    } else {
      const matched = item.variation?.find((v) => v.id === size.id);
      setVariation(matched || null);
      getColors(size);
    }
  };

  const handleAddToOrder = async () => {
    if (!variation) {
      toast.error("Please select both a color and a size");
      return;
    }

    setAdding(true);
    try {
      await axios.post(`alibaba/api/v1/orderItem/${wege_order_id}/`, {
        variation_id: variation.id,
        quantity: quantity,
      });
      toast.success("Product successfully added to order!");
      message.success("Product added to order");
      if (getOrder) {
        await getOrder();
      }
    } catch (e) {
      console.error("Error adding product to order:", e);
      if (e.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(() => handleAddToOrder());
        return;
      } else if (e.response?.status === 401 && !Cookies.get("logss")) {
        navigate("/");
        return;
      }
      const err = e.response?.data?.message || e.response?.data?.detail || "Failed to add item to order";
      toast.error(err);
      message.error(err);
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setItem(itemm);
    clearSelection();
  }, [clearSelection, itemm]);

  useEffect(() => {
    setImage(item.images?.split(",")[0] || "");
  }, [item.images]);

  const displayedColors = hideVariation
    ? availableColors.slice(0, 6)
    : availableColors;
  const remainingColorsCount = availableColors.length - 6;

  const currentPrice = variation?.price || item.price || 0;
  const currentStock = variation?.stock !== undefined ? variation.stock : 0;
  const totalPrice = (Number(currentPrice) * quantity).toFixed(2);

  return (
    <div
      style={{
        background: isDarkMode ? "#1f1f1f" : "#ffffff",
        color: isDarkMode ? "#ffffff" : "#1f1f1f",
      }}
    >
      <Row gutter={[28, 28]}>
        {/* Left: Product Images */}
        <Col xs={24} md={11} lg={10}>
          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: isDarkMode ? "1px solid #303030" : "1px solid #f0f0f0",
              background: isDarkMode ? "#141414" : "#fcfcfc",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              textAlign: "center",
              position: "relative",
            }}
          >
            <Image
              src={getImageUrl(image)}
              alt={item.name}
              style={{
                width: "100%",
                height: 420,
                objectFit: "contain",
                padding: 12,
              }}
              fallback="https://placehold.co/400x400?text=No+Image"
              preview={{
                mask: (
                  <Space>
                    <EyeOutlined />
                    <span>Zoom Preview</span>
                  </Space>
                ),
              }}
            />
          </div>

          {/* Multiple image gallery thumbnails */}
          {allImages.length > 1 && (
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 12,
                overflowX: "auto",
                paddingBottom: 4,
              }}
            >
              {allImages.slice(0, 8).map((imgUrl, idx) => (
                <div
                  key={idx}
                  onClick={() => setImage(imgUrl)}
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 8,
                    overflow: "hidden",
                    cursor: "pointer",
                    border:
                      image === imgUrl
                        ? "2px solid #1677ff"
                        : isDarkMode
                        ? "1px solid #434343"
                        : "1px solid #e2e8f0",
                    opacity: image === imgUrl ? 1 : 0.7,
                    transition: "all 0.2s ease",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={getImageUrl(imgUrl)}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
              ))}
            </div>
          )}
        </Col>

        {/* Right: Product Details & Variation Selectors */}
        <Col xs={24} md={13} lg={14}>
          <div>
            {/* Title & Badges */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {item.id && <Tag color="purple">ID: {item.id}</Tag>}
                {item.store && <Tag color="blue">Store #{item.store}</Tag>}
                {variation && <Tag color="cyan">Variation ID: {variation.id}</Tag>}
              </div>

              <Title
                level={4}
                style={{
                  margin: "0 0 6px 0",
                  color: isDarkMode ? "#f1f5f9" : "#0f172a",
                  lineHeight: 1.4,
                  fontSize: 18,
                }}
              >
                {showDetails || item.name?.length < 75
                  ? item.name
                  : `${item.name?.slice(0, 75)}...`}
              </Title>

              {item.name?.length >= 75 && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setShowDetails(!showDetails)}
                  style={{ padding: 0, height: "auto", fontSize: 13, fontWeight: 500 }}
                >
                  {showDetails ? "Show Less" : "See All"}
                </Button>
              )}

              {item.name_ar && item.name_ar !== "RTL" && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {item.name_ar}
                  </Text>
                </div>
              )}
            </div>

            <Divider style={{ margin: "14px 0" }} />

            {/* Colors Section */}
            {availableColors.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: 13,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isDarkMode ? "#cbd5e1" : "#475569",
                    }}
                  >
                    {t("orderDetails.selectColor", "Select Color")}
                  </Text>
                  {variation?.color?.name && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {t("orderDetails.selected", "Selected:")} <strong>{variation.color.name}</strong>
                    </Text>
                  )}
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                  {displayedColors.map((color) => {
                    const isSelected =
                      variation?.color?.name === color.color?.name ||
                      variation?.color?.id === color.color?.id;
                    const isOutOfStock = color.stock <= 0;

                    const colorImg =
                      (!color.images || color.images === "") && availableColors.length === 1
                        ? item.images?.split(",")[0]
                        : color.images;

                    return (
                      <Tooltip
                        key={color.id}
                        title={
                          isOutOfStock
                            ? `${color.color?.name || "Color"} (Out of Stock)`
                            : color.color?.name || "Color"
                        }
                      >
                        <div
                          onClick={() => handleSelectColor(color)}
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            position: "relative",
                            overflow: "hidden",
                            cursor: isOutOfStock ? "not-allowed" : "pointer",
                            border: isSelected
                              ? "3px solid #1677ff"
                              : isDarkMode
                              ? "2px solid #434343"
                              : "2px solid #e2e8f0",
                            boxShadow: isSelected
                              ? "0 0 0 2px rgba(22, 119, 255, 0.25)"
                              : "none",
                            opacity: isOutOfStock ? 0.35 : 1,
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            background: isDarkMode ? "#262626" : "#f1f5f9",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={getImageUrl(colorImg)}
                            alt={color.color?.name || "color"}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          {isSelected && (
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(15, 23, 42, 0.5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckOutlined style={{ color: "#ffffff", fontSize: 16 }} />
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}

                  {remainingColorsCount > 0 && hideVariation && (
                    <Button
                      type="dashed"
                      shape="circle"
                      size="middle"
                      onClick={() => setHideVariation(false)}
                      style={{ fontWeight: 600, fontSize: 12 }}
                      title="Show more colors"
                    >
                      +{remainingColorsCount}
                    </Button>
                  )}
                  {!hideVariation && availableColors.length > 6 && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setHideVariation(true)}
                    >
                      Show Less
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Size Section */}
            {availableSizes.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: 13,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isDarkMode ? "#cbd5e1" : "#475569",
                    }}
                  >
                    {t("orderDetails.selectSize", "Select Size")}
                  </Text>
                  {variation?.size?.name && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {t("orderDetails.selected", "Selected:")} <strong>{variation.size.name}</strong>
                    </Text>
                  )}
                </div>

                <Row gutter={[10, 10]}>
                  {availableSizes.map((size) => {
                    const isSelected =
                      variation?.size?.name === size.size?.name ||
                      variation?.size?.id === size.size?.id;
                    const isOutOfStock = size.stock <= 0;

                    return (
                      <Col xs={12} sm={12} key={size.id}>
                        <button
                          type="button"
                          disabled={isOutOfStock}
                          onClick={() => handleSelectSize(size)}
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: isSelected ? 600 : 500,
                            textAlign: "center",
                            cursor: isOutOfStock ? "not-allowed" : "pointer",
                            transition: "all 0.15s ease",
                            border: isSelected
                              ? "1.5px solid #0f172a"
                              : isDarkMode
                              ? "1px solid #303030"
                              : "1px solid #cbd5e1",
                            background: isSelected
                              ? "#1e293b"
                              : isDarkMode
                              ? "#262626"
                              : "#f8fafc",
                            color: isSelected
                              ? "#ffffff"
                              : isOutOfStock
                              ? "#94a3b8"
                              : isDarkMode
                              ? "#e2e8f0"
                              : "#1e293b",
                            opacity: isOutOfStock ? 0.45 : 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            lineHeight: 1.3,
                          }}
                        >
                          {isSelected && <CheckOutlined style={{ fontSize: 12 }} />}
                          <span style={{ wordBreak: "break-word" }}>
                            {size.size?.name || "Standard"}
                          </span>
                        </button>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            {/* Quantity, Live Price & Stock Panel */}
            <div
              style={{
                background: isDarkMode ? "#141414" : "#f8fafc",
                border: isDarkMode ? "1px solid #303030" : "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "16px 18px",
                marginBottom: 20,
              }}
            >
              <Row gutter={[16, 16]} align="middle" justify="space-between">
                <Col xs={24} sm={12}>
                  <Text
                    strong
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      display: "block",
                      marginBottom: 8,
                      letterSpacing: "0.5px",
                      color: isDarkMode ? "#94a3b8" : "#64748b",
                    }}
                  >
                    {t("orderDetails.quantity", "Quantity")}
                  </Text>
                  <Space.Compact orientation="horizontal">
                    <Button
                      icon={<MinusOutlined />}
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      style={{ height: 38 }}
                    />
                    <InputNumber
                      min={1}
                      max={variation?.stock && variation.stock > 0 ? variation.stock : 999}
                      value={quantity}
                      onChange={(val) => setQuantity(Number(val) || 1)}
                      style={{ width: 70, textAlign: "center", height: 38 }}
                    />
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => setQuantity(quantity + 1)}
                      disabled={variation?.stock && quantity >= variation.stock}
                      style={{ height: 38 }}
                    />
                  </Space.Compact>
                </Col>

                <Col xs={24} sm={12}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>
                      <Text type="secondary">{t("orderDetails.unitPriceLabel", "Unit Price:")} </Text>
                      <Text strong style={{ fontSize: 16 }}>
                        AED {currentPrice}
                      </Text>
                    </div>

                    <div style={{ marginBottom: 6 }}>
                      <Tag
                        color={
                          currentStock > 20
                            ? "success"
                            : currentStock > 0
                            ? "warning"
                            : "error"
                        }
                        style={{ fontSize: 12, padding: "2px 8px" }}
                      >
                        {t("orderDetails.availableStock", { stock: currentStock, defaultValue: `Available Stock: ${currentStock}` })}
                      </Tag>
                    </div>

                    {quantity > 1 && (
                      <div style={{ fontSize: 14 }}>
                        <Text type="secondary">{t("orderDetails.total", "Total:")} </Text>
                        <Text strong style={{ color: "#1677ff", fontSize: 16 }}>
                          AED {totalPrice}
                        </Text>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div>
              <Button
                type="primary"
                size="large"
                block
                icon={<ShoppingOutlined style={{ fontSize: 18 }} />}
                loading={adding}
                disabled={!variation || currentStock <= 0}
                onClick={handleAddToOrder}
                style={{
                  height: 48,
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  borderRadius: 8,
                  background:
                    !variation || currentStock <= 0
                      ? undefined
                      : "linear-gradient(135deg, #ff6b6b 0%, #fa5252 100%)",
                  borderColor:
                    !variation || currentStock <= 0 ? undefined : "#fa5252",
                  boxShadow:
                    !variation || currentStock <= 0
                      ? "none"
                      : "0 4px 14px rgba(250, 82, 82, 0.35)",
                }}
              >
                {!variation
                  ? t("orderDetails.selectColorAndSize", "SELECT COLOR & SIZE TO ORDER")
                  : currentStock <= 0
                  ? t("orderDetails.outOfStock", "OUT OF STOCK")
                  : t("orderDetails.addToOrder", "ADD TO ORDER")}
              </Button>

              {variation && (
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <Button
                    type="link"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={clearSelection}
                    style={{ color: "#8c8c8c" }}
                  >
                    {t("orderDetails.resetSelection", "Reset Selection")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
